import { axiosInventoryInstance } from '@/config/apiConfig';
import { ALL_SUB_APPLICATION } from '@/constant/global/global.constant';
import { Subapplication } from '@/store/types';
import { AxiosResponse } from 'axios';

// API base URL for Smart Cooler Products
const SMART_COOLER_PRODUCTS_BASE_URL = '/cvproducts';

// Types for Smart Cooler Product
export interface ISmartCoolerProduct {
  smartCoolerProductId: string;
  lintedSmartCoolerProductId: string;
  productId: string;
  subAppId: string;
  barcodes: string[];
  productName?: string;
  shortProductName?: string;
  productImage?: string;
  legacyId?: string;
  transactionId: string;
  lastChangeDtm: string;
  createdOn: string;
  updatedOn: string;
  deletedOn?: string | null;
  subapplication?: Subapplication;
}

export interface ISmartCoolerProductListResponse {
  data: ISmartCoolerProduct[];
  pagination: {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    currentLimit: number;
    nextPage?: number;
    previousPage?: number;
  };
}

export interface IFetchSmartCoolerProductParams {
  page?: number;
  limit?: number;
  name?: string;
  barcode?: string;
  legacyId?: string;
  application?: string;
  active?: boolean;
  orderBy?: string;
  order?: string;
  subAppId?: string;
  productIdIdentifier?: string;
  createdStartDate?: string;
  createdEndDate?: string;
  includeInactive?: boolean;
  inactive?: boolean;
}

// ========== API Methods ========== //

// GET all Smart Cooler Products (with filters/pagination)
export const fetchSmartCoolerProducts = ({
  page,
  limit,
  name,
  barcode,
  legacyId,
  application,
  active,
  order,
  orderBy,
  subAppId,
  productIdIdentifier,
  createdStartDate,
  createdEndDate,
  includeInactive,
  inactive,
}: IFetchSmartCoolerProductParams): Promise<
  AxiosResponse<{
    success: boolean;
    data: ISmartCoolerProductListResponse;
    message?: string;
  }>
> => {
  // return axiosInventoryInstance.get(SMART_COOLER_PRODUCTS_BASE_URL, { params });
  return axiosInventoryInstance({
    url: `${SMART_COOLER_PRODUCTS_BASE_URL}`,
    method: 'GET',
    params: {
      page,
      limit,
      ...(name && name.length > 0 ? { name } : {}),
      ...(barcode && barcode.length ? { barcode } : {}),
      ...(productIdIdentifier && productIdIdentifier.length
        ? { productIdIdentifier }
        : {}),
      ...(createdEndDate && createdEndDate.length ? { createdEndDate } : {}),
      ...(createdStartDate && createdStartDate.length
        ? { createdStartDate }
        : {}),
      ...(subAppId !== ALL_SUB_APPLICATION.id ? { subAppId } : {}),
      // includeInactive,
      inactive,
      orderBy,
      order,
    },
  });
};

// GET Smart Cooler Product by ID
export const fetchSmartCoolerProductById = (
  smartCoolerProductId: string,
): Promise<
  AxiosResponse<{
    success: boolean;
    data: ISmartCoolerProduct;
    message?: string;
  }>
> => {
  return axiosInventoryInstance({
    url: `${SMART_COOLER_PRODUCTS_BASE_URL}/${smartCoolerProductId}`,
    method: 'GET',
  });
};

// CREATE Smart Cooler Product
export interface ICreateSmartCoolerProductPayload {
  subAppId: string;
  barcodes: string[];
  productName?: string;
  shortProductName?: string;
  smartCoolerProductId: string;
  productImage?: string;
  legacyId?: string;
  transactionId: string;
  lastChangeDtm: string;
}

export const createSmartCoolerProduct = (
  payload: ICreateSmartCoolerProductPayload,
): Promise<
  AxiosResponse<{
    success: boolean;
    message: string;
    data: ISmartCoolerProduct;
  }>
> => {
  return axiosInventoryInstance.post(SMART_COOLER_PRODUCTS_BASE_URL, payload);
};

// UPDATE Smart Cooler Product
export interface IUpdateSmartCoolerProductPayload {
  productName?: string;
  shortProductName?: string;
  productImage?: string;
  barcodes?: string[];
  legacyId?: string;
  lastChangeDtm?: string;
}

export const updateSmartCoolerProduct = (
  id: string,
  payload: IUpdateSmartCoolerProductPayload,
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return axiosInventoryInstance.patch(
    `${SMART_COOLER_PRODUCTS_BASE_URL}/${id}`,
    payload,
  );
};

// DELETE Smart Cooler Product (soft delete)
export const deleteSmartCoolerProduct = (
  smartCoolerProductId: string,
  transactionId: string,
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return axiosInventoryInstance.delete(
    `${SMART_COOLER_PRODUCTS_BASE_URL}/${smartCoolerProductId}`,
    {
      data: { transactionId },
    },
  );
};

export const activateSmartCoolerProduct = (
  smartCoolerProductId: string,
  transactionId: string,
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return axiosInventoryInstance.patch(
    `${SMART_COOLER_PRODUCTS_BASE_URL}/restore/${smartCoolerProductId}`,
    { transactionId },
  );
};

export const getUserDataByAuth0Id = (
  authUserId: string,
): Promise<AxiosResponse<{ success: boolean; message: string; data: any }>> => {
  return axiosInventoryInstance.get(
    `/users/auth0/search?page=1&limit=10&inactive=true&includeInactive=true&auth0UserId=${authUserId}`,
  );
};
